import { cn } from "@/lib/cn";
import type { BotStatus } from "@/types";

/**
 * Variantes v2.1 — alineadas con `state-{bot,human,escalated,closed}` y los
 * accent colors del design system. Sin glow ni shadow.
 */
export type StatusDotVariant =
  | "bot"
  | "human"
  | "escalated"
  | "closed"
  | "success"
  | "warning"
  | "error"
  | "muted";

interface BaseProps {
  className?: string;
  /** Tamaño del dot en px. Default 8. */
  size?: number;
  /** Pulso sutil para indicar actividad. Default false (sin animación). */
  pulse?: boolean;
}

interface VariantProps extends BaseProps {
  variant: StatusDotVariant;
  /** Texto auxiliar a la derecha del dot. */
  label?: string;
  state?: never;
  withLabel?: never;
}

interface BotStateProps extends BaseProps {
  /** Compat — mapea `BotStatus["state"]` a un variant. */
  state: BotStatus["state"];
  withLabel?: boolean;
  variant?: never;
  label?: never;
}

type StatusDotProps = VariantProps | BotStateProps;

const VARIANT_BG: Record<StatusDotVariant, string> = {
  bot: "bg-info",
  human: "bg-magenta",
  escalated: "bg-warning",
  closed: "bg-text-disabled",
  success: "bg-success",
  warning: "bg-warning",
  error: "bg-error",
  muted: "bg-text-disabled",
};

const BOT_STATE_MAP: Record<
  BotStatus["state"],
  { variant: StatusDotVariant; label: string; pulse: boolean }
> = {
  running: { variant: "success", label: "ok", pulse: true },
  degraded: { variant: "warning", label: "lento", pulse: true },
  paused: { variant: "muted", label: "pausado", pulse: false },
  killed: { variant: "error", label: "off", pulse: true },
};

/**
 * Punto coloreado simple para indicar estado.
 *
 * v2.1: sin glow, sin shadow — solo color + tamaño + pulse opcional.
 *
 * @example
 * <StatusDot variant="bot" />
 * <StatusDot variant="human" label="humano" />
 * <StatusDot variant="success" pulse />
 *
 * // Compat con BotStatus
 * <StatusDot state={status.state} withLabel />
 */
export function StatusDot(props: StatusDotProps) {
  const { className, size = 8, pulse: pulseProp } = props;

  let variant: StatusDotVariant;
  let label: string | undefined;
  let pulse = pulseProp ?? false;

  if ("state" in props && props.state) {
    const mapped = BOT_STATE_MAP[props.state];
    variant = mapped.variant;
    if (props.withLabel) label = mapped.label;
    if (pulseProp === undefined) pulse = mapped.pulse;
  } else {
    variant = (props as VariantProps).variant;
    label = (props as VariantProps).label;
  }

  const dot = (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block rounded-full shrink-0",
        VARIANT_BG[variant],
        pulse && "animate-pulse-soft",
      )}
      style={{ width: size, height: size }}
    />
  );

  if (!label) {
    return <span className={cn("inline-flex items-center", className)}>{dot}</span>;
  }

  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      {dot}
      <span className="font-mono text-2xs uppercase tracking-wide text-text-secondary">
        {label}
      </span>
    </span>
  );
}
